/**
 * Headless wallboard logic — formatters, freshness classification,
 * rotation hook. Platform-neutral: no DOM, no Radix, no router.
 *
 * The page composes these primitives with shell-owned styling and
 * components. Putting them here keeps the shell free of business
 * logic and makes a future native shell trivial.
 */

import { useEffect, useState } from "react";
import { currentTime } from "./current-time";

/**
 * Format a monetary value for the wallboard.
 *
 * Wallboard figures are large and read at distance. Compact form
 * ("£1.4M", "£650k") is the default; pass `precise: true` to render
 * the full pound figure for the headline panel.
 */
export function formatMoney(
  value: number,
  opts: { precise?: boolean; signed?: boolean } = {},
): string {
  const sign =
    opts.signed && value !== 0 ? (value > 0 ? "+" : "−") : value < 0 ? "−" : "";
  const abs = Math.abs(value);
  if (opts.precise) {
    return `${sign}£${abs.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
  }
  if (abs >= 1_000_000) {
    return `${sign}£${(abs / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  }
  if (abs >= 1_000) {
    return `${sign}£${Math.round(abs / 1_000).toLocaleString("en-GB")}k`;
  }
  return `${sign}£${abs.toLocaleString("en-GB")}`;
}

/** Format a fraction as a 0-decimal percent. */
export function formatPercent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

/** "09:31" from an ISO timestamp. Always renders in local time. */
export function formatTimeOfDay(iso: string): string {
  const d = new Date(iso);
  return `${d.getHours().toString().padStart(2, "0")}:${d
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
}

export type FreshnessTier = "fresh" | "stale" | "ancient";

/**
 * Classify how fresh a snapshot is at a given reference time.
 *
 * - `fresh` — assembled within the last 10 minutes. Normal display.
 * - `stale` — older than 10 minutes; show an explicit indicator so a
 *   frozen screen is obviously frozen rather than wrongly trusted.
 * - `ancient` — older than 60 minutes; the screen flips to
 *   last-known-good mode and surfaces the timestamp prominently.
 *
 * The thresholds are deliberately generous: the underlying data
 * refresh cadence is minutes, not seconds, and a brief blip in the
 * data feed should not visually alarm the screen.
 */
export function classifyFreshness(
  asAtIso: string,
  nowMs: number,
): FreshnessTier {
  const ageMs = nowMs - new Date(asAtIso).getTime();
  if (ageMs < 10 * 60 * 1000) return "fresh";
  if (ageMs < 60 * 60 * 1000) return "stale";
  return "ancient";
}

/**
 * Rotation hook for the supporting strip.
 *
 * Given N panels and an interval (ms), returns the current panel
 * index and cycles deterministically. Pauses when the document is
 * hidden so a tab that the OS has backgrounded does not race ahead.
 *
 * Tests inject `nowProvider` for determinism; the default uses
 * window.setTimeout (platform-neutral via React state). The hook
 * does not touch `document` directly — visibility pausing is opt-in
 * via the optional `isPageHidden` argument the shell wires through
 * the capability port if needed. For v1, the shell passes `false`.
 */
export function useRotation(
  panelCount: number,
  intervalMs: number,
  isPageHidden = false,
): number {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (panelCount <= 1 || isPageHidden) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % panelCount);
    }, intervalMs);
    return () => clearInterval(id);
  }, [panelCount, intervalMs, isPageHidden]);
  // Clamp on shrinking panelCount.
  return panelCount === 0 ? 0 : index % panelCount;
}

/**
 * Tick at a steady cadence so derived freshness re-classifies without
 * a new snapshot arriving. Returns the current epoch ms, defaulting
 * to a 30s tick (good enough for minute-resolution freshness).
 */
export function useNowTicker(periodMs = 30_000): number {
  const [now, setNow] = useState<number>(() => currentTime().getTime());
  useEffect(() => {
    const id = setInterval(() => setNow(currentTime().getTime()), periodMs);
    return () => clearInterval(id);
  }, [periodMs]);
  return now;
}
